import { LabelGenerator, LabelGeneratorMap, DefaultLabelGenerator } from './types';
import { TreeNodeType } from '../data_nodes/types';

/**
 * Global registry for alt label generators
 */
const altLabelGeneratorMap: LabelGeneratorMap = {};

/**
 * Global registry for default label generators
 */
const defaultLabelGeneratorMap: Record<string, DefaultLabelGenerator> = {};

/**
 * Register an alt label generator for a specific node type
 */
export function registerAltLabelGenerator(nodeType: TreeNodeType, generator: LabelGenerator): void {
  altLabelGeneratorMap[nodeType] = generator;
}

/**
 * Register a default label generator for a specific node type
 */
export function registerDefaultLabelGenerator(
  nodeType: TreeNodeType,
  generator: DefaultLabelGenerator,
): void {
  defaultLabelGeneratorMap[nodeType] = generator;
}

/**
 * Get the alt label generator for a node type
 * Returns null if no generator is registered
 */
export function getAltLabelGenerator(nodeType: string): LabelGenerator | null {
  return altLabelGeneratorMap[nodeType] || null;
}

/**
 * Get the default label generator for a node type
 * Returns null if no generator is registered
 */
export function getDefaultLabelGenerator(nodeType: string): DefaultLabelGenerator | null {
  return defaultLabelGeneratorMap[nodeType] || null;
}

/**
 * Get all registered node types for alt label generators
 */
export function getAllAltLabelGeneratorTypes(): string[] {
  return Object.keys(altLabelGeneratorMap);
}

/**
 * Get all registered node types for default label generators
 */
export function getAllDefaultLabelGeneratorTypes(): string[] {
  return Object.keys(defaultLabelGeneratorMap);
}

/**
 * Check if an alt label generator is registered for a node type
 */
export function hasAltLabelGenerator(nodeType: string): boolean {
  return nodeType in altLabelGeneratorMap;
}

/**
 * Check if a default label generator is registered for a node type
 */
export function hasDefaultLabelGenerator(nodeType: string): boolean {
  return nodeType in defaultLabelGeneratorMap;
}

/**
 * Clear all registered generators (useful for testing)
 */
export function clearAllGenerators(): void {
  Object.keys(altLabelGeneratorMap).forEach((key) => delete altLabelGeneratorMap[key]);
  Object.keys(defaultLabelGeneratorMap).forEach((key) => delete defaultLabelGeneratorMap[key]);
}

/**
 * Get registry statistics for debugging
 */
export function getRegistryStats(): {
  altGeneratorCount: number;
  defaultGeneratorCount: number;
  registeredTypes: string[];
} {
  return {
    altGeneratorCount: Object.keys(altLabelGeneratorMap).length,
    defaultGeneratorCount: Object.keys(defaultLabelGeneratorMap).length,
    registeredTypes: Array.from(
      new Set([...Object.keys(altLabelGeneratorMap), ...Object.keys(defaultLabelGeneratorMap)]),
    ),
  };
}
